import React from "react";
import {
  ChevronUp,
  ChevronDown,
  Pencil,
  Copy,
  Trash2,
  Plus,
  Image as ImageIcon,
  Star,
} from "lucide-react";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import TintedImage from "./TintedImage";
import { STEP_TYPES, getImageUrl } from "./configuratorConstants";

export default function StepRow({
  step,
  index,
  activeStepIndex,
  setActiveStepIndex,
  isDark,
  stepsLength,
  openEditStep,
  duplicateStep,
  handleDeleteStep,
  moveStep,
  openAddOption,
  openEditOption,
  duplicateOption,
  handleDeleteOption,
  moveOption,
  setDefaultOption,
}) {
  return (
    <>
      {/* Step header */}
      <div
        className="flex flex-col sm:flex-row sm:items-center px-3 sm:px-4 py-3 cursor-pointer gap-2 sm:gap-0"
        onClick={() =>
          setActiveStepIndex(activeStepIndex === index ? null : index)
        }
      >
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <div className="flex flex-col items-center space-y-0.5 flex-shrink-0">
            <button
              onClick={(e) => {
                e.stopPropagation();
                moveStep(index, -1);
              }}
              className={`disabled:opacity-30 ${isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
              disabled={index === 0}
            >
              <ChevronUp className="h-3 w-3" />
            </button>
            <span className="text-xs font-medium text-gray-400">
              {index + 1}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                moveStep(index, 1);
              }}
              className={`disabled:opacity-30 ${isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
              disabled={index === stepsLength - 1}
            >
              <ChevronDown className="h-3 w-3" />
            </button>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center flex-wrap gap-1.5">
              <h3
                className={`font-semibold truncate ${isDark ? "text-white" : "text-gray-900"}`}
              >
                {step.name}
              </h3>
              <Badge
                variant="secondary"
                className={`text-xs flex-shrink-0 ${isDark ? "bg-white/[0.06] text-gray-400" : "bg-gray-100"}`}
              >
                {STEP_TYPES.find((t) => t.value === step.type)?.label ||
                  step.type}
              </Badge>
              {step.required && (
                <Badge className="text-xs flex-shrink-0 bg-[#BFFF00] text-[#05081A] hover:bg-[#BFFF00]">
                  Verplicht
                </Badge>
              )}
              {step.hiddenLayer && (
                <Badge className="text-xs flex-shrink-0 bg-purple-100 text-purple-700 hover:bg-purple-100">
                  Verborgen laag
                </Badge>
              )}
            </div>
            <p
              className={`text-sm mt-0.5 truncate ${isDark ? "text-gray-400" : "text-gray-500"}`}
            >
              {step.type === "frame-color"
                ? `${(step.zones || []).filter((z) => z.enabled).length} zones`
                : `${step.options?.length || 0} opties`}
              {step.description && ` · ${step.description}`}
            </p>
          </div>
          {/* Chevron indicator - mobile only */}
          <div className="sm:hidden flex-shrink-0">
            {activeStepIndex === index ? (
              <ChevronUp className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            )}
          </div>
        </div>
        <div className="flex items-center space-x-1 sm:ml-4 pl-8 sm:pl-0">
          <Button
            variant="ghost"
            size="icon"
            className={`h-8 w-8 ${isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
            onClick={(e) => {
              e.stopPropagation();
              openEditStep(step);
            }}
            title="Stap bewerken"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className={`h-8 w-8 ${isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
            onClick={(e) => {
              e.stopPropagation();
              duplicateStep(step);
            }}
            title="Stap dupliceren"
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-gray-400 hover:text-red-600"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteStep(step.id);
            }}
            title="Stap verwijderen"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
          {/* Chevron indicator - desktop only */}
          <div className="ml-2 hidden sm:block">
            {activeStepIndex === index ? (
              <ChevronUp className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            )}
          </div>
        </div>
      </div>

      {/* Step options (collapsible) */}
      {activeStepIndex === index && (
        <div
          className={`border-t px-4 py-4 ${isDark ? "border-white/[0.06] bg-white/[0.03]" : "border-gray-100 bg-gray-50/50"}`}
        >
          {/* Frame-color: hint when no options yet */}
          {step.type === "frame-color" &&
            (!step.options || step.options.length === 0) && (
              <div
                className={`rounded-lg border p-4 mb-4 text-center ${isDark ? "border-white/[0.06] bg-white/[0.02]" : "border-gray-200 bg-gray-50"}`}
              >
                <p
                  className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}
                >
                  Voeg opties toe om framekleuren te configureren. Elke optie
                  bevat een frame afbeelding (of SVG) en standaardkleur.
                </p>
              </div>
            )}

          {/* Options list */}
          <>
            {step.options && step.options.length > 0 ? (
              <div className="space-y-2 mb-4">
                {step.options.map((option, optIndex) => (
                  <div
                    key={option.id}
                    className={`border rounded-lg px-3 py-2.5 transition-all group ${isDark ? "bg-white/[0.03] border-white/[0.06] hover:border-white/10" : "bg-white border-gray-100 hover:border-gray-200"}`}
                  >
                    <div className="flex items-center">
                      {/* Option image/color preview */}
                      <div className="mr-3 flex-shrink-0">
                        {step.type === "frame-color" &&
                        option.image &&
                        option.colorHex ? (
                          <div className="h-10 w-10 rounded-lg overflow-hidden bg-white">
                            <TintedImage
                              src={getImageUrl(option.image)}
                              color={option.colorHex}
                              color2={option.colorHex2 || undefined}
                              gradientAngle={option.gradientAngle}
                              alt={option.name}
                              className="h-10 w-10 object-contain"
                            />
                          </div>
                        ) : step.type === "frame-color" && option.colorHex ? (
                          <div
                            className={`h-10 w-10 rounded-lg border-2 ${isDark ? "border-white/10" : "border-gray-200"}`}
                            style={{
                              background: option.colorHex2
                                ? `linear-gradient(${option.gradientAngle ?? 90}deg, ${option.colorHex}, ${option.colorHex2})`
                                : option.colorHex,
                            }}
                          />
                        ) : step.type === "color" && option.colorHex ? (
                          <div
                            className={`h-10 w-10 rounded-lg border-2 ${isDark ? "border-white/10" : "border-gray-200"}`}
                            style={{
                              background: option.colorHex2
                                ? `linear-gradient(${option.gradientAngle ?? 90}deg, ${option.colorHex}, ${option.colorHex2})`
                                : option.colorHex,
                            }}
                          />
                        ) : option.image ? (
                          <img
                            src={getImageUrl(option.image)}
                            alt={option.name}
                            className="h-10 w-10 rounded-lg object-cover"
                          />
                        ) : (
                          <div
                            className={`h-10 w-10 rounded-lg flex items-center justify-center ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
                          >
                            <ImageIcon className="h-4 w-4 text-gray-400" />
                          </div>
                        )}
                      </div>

                      {/* Option info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center flex-wrap gap-1">
                          <span
                            className={`font-medium text-sm ${isDark ? "text-white" : "text-gray-900"}`}
                          >
                            {option.name}
                          </span>
                          {step.defaultOptionId === option.id && (
                            <Badge className="text-xs bg-amber-100 text-amber-700 hover:bg-amber-100">
                              <Star className="h-2.5 w-2.5 mr-0.5 inline fill-amber-500" />
                              Default
                            </Badge>
                          )}
                          {!option.inStock && (
                            <Badge className="text-xs bg-red-100 text-red-700 hover:bg-red-100">
                              Niet op voorraad
                            </Badge>
                          )}
                          {option.dependencies &&
                            option.dependencies.length > 0 && (
                              <Badge className="text-xs bg-purple-100 text-purple-700 hover:bg-purple-100">
                                Voorwaardelijk
                              </Badge>
                            )}
                        </div>
                        {option.description && (
                          <p
                            className={`text-xs truncate ${isDark ? "text-gray-400" : "text-gray-500"}`}
                          >
                            {option.description}
                          </p>
                        )}
                      </div>

                      {/* Price */}
                      <div className="mx-2 sm:mx-3 text-right flex-shrink-0">
                        {option.price !== 0 && (
                          <span
                            className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}
                          >
                            {option.priceType === "addition"
                              ? option.price > 0
                                ? "+"
                                : ""
                              : ""}
                            €{Number(option.price).toFixed(2)}
                          </span>
                        )}
                        {option.price === 0 && (
                          <span className="text-xs text-gray-400">
                            Inbegrepen
                          </span>
                        )}
                      </div>

                      {/* Option actions - desktop */}
                      <div className="hidden sm:flex items-center space-x-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => moveOption(step.id, optIndex, -1)}
                          className={`p-1 disabled:opacity-30 ${isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
                          disabled={optIndex === 0}
                        >
                          <ChevronUp className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => moveOption(step.id, optIndex, 1)}
                          className={`p-1 disabled:opacity-30 ${isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
                          disabled={optIndex === step.options.length - 1}
                        >
                          <ChevronDown className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => setDefaultOption(step.id, option.id)}
                          className={`p-1 transition-colors ${
                            step.defaultOptionId === option.id
                              ? "text-amber-500 hover:text-amber-600"
                              : "text-gray-400 hover:text-amber-500"
                          }`}
                          title={
                            step.defaultOptionId === option.id
                              ? "Default verwijderen"
                              : "Als default instellen"
                          }
                        >
                          <Star
                            className={`h-3 w-3 ${
                              step.defaultOptionId === option.id
                                ? "fill-amber-500"
                                : ""
                            }`}
                          />
                        </button>
                        <button
                          onClick={() => openEditOption(step.id, option)}
                          className={`p-1 ${isDark ? "text-gray-500 hover:text-white" : "text-gray-400 hover:text-gray-700"}`}
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => duplicateOption(step.id, option)}
                          className={`p-1 ${isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => handleDeleteOption(step.id, option.id)}
                          className="p-1 text-gray-400 hover:text-red-600"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </div>

                    {/* Mobile action bar */}
                    <div
                      className={`sm:hidden flex items-center justify-between mt-2 pt-2 border-t ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
                    >
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => moveOption(step.id, optIndex, -1)}
                          className={`p-1.5 rounded-md disabled:opacity-30 ${isDark ? "text-gray-500 active:bg-white/[0.06]" : "text-gray-400 active:bg-gray-100"}`}
                          disabled={optIndex === 0}
                        >
                          <ChevronUp className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => moveOption(step.id, optIndex, 1)}
                          className={`p-1.5 rounded-md disabled:opacity-30 ${isDark ? "text-gray-500 active:bg-white/[0.06]" : "text-gray-400 active:bg-gray-100"}`}
                          disabled={optIndex === step.options.length - 1}
                        >
                          <ChevronDown className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setDefaultOption(step.id, option.id)}
                          className={`p-1.5 rounded-md transition-colors ${
                            step.defaultOptionId === option.id
                              ? "text-amber-500"
                              : isDark
                                ? "text-gray-500 active:bg-white/[0.06]"
                                : "text-gray-400 active:bg-gray-100"
                          }`}
                        >
                          <Star
                            className={`h-4 w-4 ${
                              step.defaultOptionId === option.id
                                ? "fill-amber-500"
                                : ""
                            }`}
                          />
                        </button>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => openEditOption(step.id, option)}
                          className={`p-1.5 rounded-md ${isDark ? "text-gray-400 active:bg-white/[0.06]" : "text-gray-500 active:bg-gray-100"}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => duplicateOption(step.id, option)}
                          className={`p-1.5 rounded-md ${isDark ? "text-gray-500 active:bg-white/[0.06]" : "text-gray-400 active:bg-gray-100"}`}
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteOption(step.id, option.id)}
                          className={`p-1.5 rounded-md ${isDark ? "text-gray-400 active:bg-red-500/10" : "text-gray-400 active:bg-red-50"} hover:text-red-600`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div
                className={`text-center py-6 mb-4 border-2 border-dashed rounded-lg ${isDark ? "border-white/10 bg-white/[0.03]" : "border-gray-200 bg-white"}`}
              >
                <p className="text-gray-400 text-sm">
                  Nog geen opties in deze stap
                </p>
              </div>
            )}
            <Button
              onClick={() => openAddOption(step.id)}
              variant="outline"
              size="sm"
              className={
                isDark
                  ? "border-white/[0.08] text-gray-300 hover:bg-white/[0.04]"
                  : "border-gray-200 text-gray-600 hover:bg-gray-50"
              }
            >
              <Plus className="mr-1 h-3 w-3" />
              Optie Toevoegen
            </Button>
          </>
        </div>
      )}
    </>
  );
}
